import { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import { alpha } from "@mui/material/styles";
import Checkbox from "@mui/material/Checkbox";
import InputBase from "@mui/material/InputBase";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import SearchIcon from "@mui/icons-material/Search";
import SwapVertIcon from "@mui/icons-material/SwapVert";

import ConfigEditorShell from "../../components/ConfigEditorShell/ConfigEditorShell";
import type { ExitRoute } from "../../components/ConfigEditorShell/useConfigEditor";
import { useConfigEditor } from "../../components/ConfigEditorShell/useConfigEditor";
import getIcon, { NAV_ICON_NAMES } from "../../utils/get-icon";
import * as metrics from "./designMetrics";
import type { IAction } from "../../domain/action.interface";
import type {
	ActionGroupStatus,
	IActionGroup,
} from "../../domain/action-group.interface";

/**
 * The group editor, as a FULL-PAGE VIEW.
 *
 * It was an inline `Card` pushed in above the list — not a modal, but not this either.
 * The design gives it the same treatment as the action editor: its own page under a
 * `Program setup / Actions / Groups / New group` breadcrumb, with Cancel and Create in a
 * sticky header (`Actions Pattern v2.dc.html:791`).
 *
 * Why that matters beyond tidiness: a group has a NAME, a STATUS, a DESCRIPTION, an ICON and
 * a MEMBERSHIP LIST. Squeezed above the table, the membership list had nowhere to go, so the
 * one decision the screen is really for — which permissions belong in this section — was the
 * thing with least room. On its own page it gets a card of its own.
 *
 * Built on the same `ConfigEditorShell` as `ActionEditor`, so the two editors behave alike:
 * one discard prompt, one unsaved-changes pill, one save-failed banner, and a Cancel that
 * asks before throwing work away.
 */

export interface GroupDraft extends Record<string, unknown> {
	name: string;
	description: string;
	icon: string;
	status: ActionGroupStatus;
}

export interface GroupEditorProps {
	/** The group being edited, or null when creating. */
	group: IActionGroup | null;
	/** Every action, so membership can be chosen here. */
	allActions: IAction[];
	/** Ids currently in this group. */
	memberIds: string[];
	/**
	 * The ids the group held when the editor opened.
	 *
	 * Needed because membership lives OUTSIDE `useConfigEditor` — it is a list of other
	 * records, not a field of this one — so the hook cannot see it change. Without this the
	 * editor stayed "clean" after a membership-only edit, which disabled Save and made the
	 * change impossible to commit. AC2 is precisely about committing it.
	 */
	initialMemberIds: string[];
	onMemberIdsChange: (ids: string[]) => void;
	/** Resolves an action's current group name, for the "Currently in …" line. */
	groupNameOf: (action: IAction) => string;
	onSubmit: (draft: GroupDraft) => Promise<void>;
	onExit: () => void;
	onDelete?: () => void;
}

const GroupEditor = ({
	group,
	allActions,
	memberIds,
	initialMemberIds,
	onMemberIdsChange,
	groupNameOf,
	onSubmit,
	onExit,
	onDelete,
}: GroupEditorProps) => {
	const isNew = !group;
	const [filter, setFilter] = useState("");

	/**
	 * Whether the membership differs from what the group held on open.
	 *
	 * Compared as SETS: ticking a box then unticking it leaves the group as it was, and the
	 * editor should go back to clean rather than claim a change nobody made.
	 */
	const membershipDirty = useMemo(() => {
		if (memberIds.length !== initialMemberIds.length) return true;
		const before = new Set(initialMemberIds);
		return memberIds.some((id) => !before.has(id));
	}, [memberIds, initialMemberIds]);

	const editor = useConfigEditor<GroupDraft>({
		initial: {
			name: group?.name ?? "",
			description: group?.description ?? "",
			icon: group?.icon ?? "",
			status: (group?.status as ActionGroupStatus) ?? "ACTIVE",
		},
		onSave: onSubmit,
		trimmedFields: ["name", "description"],
		fallbackSaveError: "The group could not be saved.",
		// Membership is not a field on the group, so the hook cannot see it change. Declared
		// here rather than OR'd into `isDirty` and `canSave` at the shell: the hook's own
		// `save()` guards on `isDirty` and returned early on a membership-only change, which
		// left the Save button live and inert.
		extraDirty: membershipDirty,
	});

	// `requestExit(proceed, route)` — the destination first. Clean leaves immediately; dirty
	// opens the shell's discard prompt, which is why Cancel never throws work away silently.
	const leave = (route: ExitRoute): void => {
		editor.requestExit(onExit, route);
	};

	// The shell closes the editor itself on a successful save; a failure leaves the draft
	// intact and surfaces in its banner, so there is nothing to do here but hand off.
	const handleSave = (): void => {
		void editor.save();
	};

	/**
	 * The membership list, narrowed by the filter box.
	 *
	 * Filtered on name AND code, because an administrator looking for a permission knows one
	 * or the other and rarely both.
	 */
	const visibleActions = useMemo(() => {
		const needle = filter.trim().toLowerCase();
		if (!needle) return allActions;
		return allActions.filter(
			(action) =>
				action.name.toLowerCase().includes(needle) ||
				action.code.toLowerCase().includes(needle),
		);
	}, [allActions, filter]);

	const toggleMember = (actionId: string): void => {
		onMemberIdsChange(
			memberIds.includes(actionId)
				? memberIds.filter((id) => id !== actionId)
				: [...memberIds, actionId],
		);
	};

	return (
		<ConfigEditorShell
			discardBody="This group has changes that have not been saved. Discarding closes the editor and leaves the group as it was."
			breadcrumbs={[
				{ label: "Program setup" },
				{ label: "Actions", onClick: () => leave("breadcrumb") },
				{ label: "Groups", onClick: () => leave("breadcrumb") },
			]}
			title={isNew ? "New group" : (group?.name ?? "Group")}
			isDirty={editor.isDirty}
			isSaving={editor.isSaving}
			isNew={isNew}
			/*
			 * NOT also gated on a non-blank name. C17664 requires that saving without one
			 * REFUSES and says "Name is required"; a disabled button refuses silently and
			 * leaves the author to work out which field is at fault. `saveGroup` throws that
			 * exact message and the shell renders it in its save-failed banner.
			 */
			canSave={editor.canSave}
			saveError={editor.saveError}
			failedTitle="Group wasn't saved"
			discardPromptOpen={editor.discardPromptOpen}
			onSave={handleSave}
			onDelete={group && onDelete ? onDelete : undefined}
			onCancel={() => leave("cancel")}
			onBreadcrumb={() => leave("breadcrumb")}
			onKeepEditing={editor.keepEditing}
			onDiscard={editor.discard}
		>
			<Box sx={metrics.editorCard}>
				<Typography component="h2" sx={metrics.editorCardTitle}>
					Group details
				</Typography>

				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: { xs: "1fr", md: "1fr 11.25rem" },
						gap: 2.5,
					}}
				>
					<Box>
						<Typography sx={metrics.fieldLabel}>Group name *</Typography>
						<TextField
							fullWidth
							placeholder="e.g. Screenings &amp; UDT"
							value={editor.draft.name}
							onChange={(event) => editor.setField("name", event.target.value)}
							inputProps={{ "aria-label": "Group name" }}
						/>
					</Box>
					<Box>
						<Typography sx={metrics.fieldLabel} id="group-status-label">
							Status *
						</Typography>
						<TextField
							select
							fullWidth
							value={editor.draft.status}
							onChange={(event) =>
								editor.setField(
									"status",
									event.target.value as ActionGroupStatus,
								)
							}
							/*
							 * The accessible name goes on the SELECT, not through `inputProps`.
							 *
							 * MUI renders a select as a visible combobox plus a hidden input, and
							 * `inputProps` names the HIDDEN one — so the control was findable by
							 * name but the thing found could not be clicked. Three cases that
							 * change a group's status this way failed on it, and the failure
							 * looked like a stale locator rather than a real defect.
							 */
							SelectProps={{ "aria-labelledby": "group-status-label" }}
						>
							{/*
							 * Sentence case, as the design has it. The stored value stays
							 * ACTIVE / INACTIVE — this is the label, not the data.
							 */}
							<MenuItem value="ACTIVE">Active</MenuItem>
							<MenuItem value="INACTIVE">Inactive</MenuItem>
						</TextField>
					</Box>
				</Box>

				<Box>
					<Typography sx={metrics.fieldLabel}>Description</Typography>
					<TextField
						fullWidth
						multiline
						minRows={2}
						placeholder="What this group covers."
						value={editor.draft.description}
						onChange={(event) =>
							editor.setField("description", event.target.value)
						}
						inputProps={{ "aria-label": "Description" }}
					/>
				</Box>

				<Box>
					<Typography sx={metrics.fieldLabel}>Icon</Typography>
					{/*
					 * A grid of the icons the app can actually render, not free text.
					 *
					 * Same reasoning as the action editor's: `getIcon` resolves exactly these
					 * names and returns nothing for anything else, so a typed value could be
					 * saved and then silently never appear. Offering the set makes that
					 * impossible rather than merely unlikely.
					 */}
					<Stack direction="row" flexWrap="wrap" gap={1}>
						{NAV_ICON_NAMES.map((name) => {
							const selected = editor.draft.icon === name;
							return (
								<Box
									key={name}
									component="button"
									type="button"
									title={name}
									aria-label={name}
									aria-pressed={selected}
									onClick={() => editor.setField("icon", selected ? "" : name)}
									sx={metrics.iconTile(selected)}
								>
									{getIcon(name)}
								</Box>
							);
						})}
					</Stack>
				</Box>

				{/*
				 * Position is NOT a field here, and the note says why rather than leaving a
				 * missing input to be noticed. The Groups list owns the order through its
				 * arrows; a typed position would be a second source of truth for the same
				 * thing, which is what the draft-order work exists to avoid.
				 */}
				<Stack
					direction="row"
					alignItems="flex-start"
					spacing={1.25}
					sx={{
						backgroundColor: (theme) => theme.palette.surfaceTint.main,
						borderRadius: "0.5rem",
						px: 2,
						py: 1.5,
					}}
				>
					<SwapVertIcon sx={{ fontSize: "1.25rem", color: "primary.main" }} />
					<Typography
						sx={{
							fontSize: "0.8125rem",
							lineHeight: "1.125rem",
							color: "text.secondary",
						}}
					>
						Section position is set with the up and down arrows on the Groups
						list, not typed here.
					</Typography>
				</Stack>
			</Box>

			<Box sx={{ ...metrics.editorCard, mt: 2.5 }}>
				<Stack direction="row" alignItems="center" spacing={2}>
					<Box>
						<Typography component="h2" sx={metrics.editorCardTitle}>
							Actions in this group
						</Typography>
						<Typography
							sx={{ fontSize: "0.8125rem", color: "text.secondary", mt: "2px" }}
						>
							{`${memberIds.length} ${
								memberIds.length === 1 ? "action" : "actions"
							} in this group`}
						</Typography>
					</Box>
					<Box sx={{ flex: 1 }} />
					<Stack
						direction="row"
						alignItems="center"
						spacing={1}
						sx={{
							backgroundColor: (theme) => theme.palette.surface.main,
							borderRadius: "0.5rem",
							height: "2.5rem",
							px: 1.5,
							width: "17.5rem",
							maxWidth: "100%",
						}}
					>
						<SearchIcon
							sx={{ fontSize: "1.125rem", color: "text.secondary" }}
						/>
						<InputBase
							value={filter}
							onChange={(event) => setFilter(event.target.value)}
							placeholder="Filter actions"
							inputProps={{ "aria-label": "Filter actions" }}
							sx={{ flex: 1, fontSize: "0.8125rem" }}
						/>
					</Stack>
				</Stack>

				{/*
				 * The list SCROLLS inside its own bordered box — `max-height: 340px;
				 * overflow-y: auto` (`Actions Pattern v2.dc.html:848`).
				 *
				 * Without it the card grows with the catalogue: on this account that is 89
				 * actions, so the page ran on for screens and the CREATE button in the sticky
				 * header was the only thing you could still reach. The cap is what keeps the
				 * editor a page rather than a scroll.
				 */}
				<Box
					sx={{
						border: "1px solid",
						borderColor: (theme) => theme.palette.outline.main,
						borderRadius: "0.5rem",
						maxHeight: "21.25rem",
						overflowY: "auto",
					}}
				>
					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
						}}
					>
						{visibleActions.map((action) => {
							const current = groupNameOf(action);
							return (
								<Stack
									key={action._id}
									direction="row"
									alignItems="flex-start"
									spacing={1.25}
									onClick={() => toggleMember(action._id)}
									/*
									 * The WHOLE ROW toggles, not just the checkbox — the design puts
									 * the handler on the row (`:851`). With two columns of small
									 * checkboxes, aiming at the box is the slowest part of the job,
									 * and the row is the thing being chosen.
									 */
									sx={{
										px: 2,
										py: 1.25,
										cursor: "pointer",
										borderBottom: "1px solid",
										borderColor: "divider",
										"&:hover": {
											backgroundColor: (theme) =>
												alpha(theme.palette.primary.main, 0.06),
										},
									}}
								>
									<Checkbox
										size="small"
										checked={memberIds.includes(action._id)}
										// The row owns the click; this keeps the box from firing twice.
										onClick={(event) => event.stopPropagation()}
										onChange={() => toggleMember(action._id)}
										inputProps={{ "aria-label": `Include ${action.name}` }}
										sx={{ p: 0, mt: "1px" }}
									/>
									<Box sx={{ flex: 1, minWidth: 0 }}>
										<Stack direction="row" alignItems="center" spacing={1}>
											<Typography
												sx={{ fontSize: "0.875rem", fontWeight: 500 }}
											>
												{action.name}
											</Typography>
											<Box
												component="span"
												sx={{
													fontFamily: "'SF Mono', Menlo, Consolas, monospace",
													fontSize: "0.625rem",
													backgroundColor: (theme) =>
														theme.palette.surface.main,
													border: "1px solid",
													borderColor: "divider",
													borderRadius: "0.25rem",
													px: "5px",
													py: "1px",
													color: "text.secondary",
												}}
											>
												{action.code}
											</Box>
										</Stack>
										{/*
										 * "Currently in <group>" — the design shows it, and it is the
										 * one thing that makes ticking a box here safe to do. Moving
										 * an action into this group takes it OUT of another, and
										 * without this line that consequence is invisible.
										 */}
										<Typography
											sx={{ fontSize: "0.6875rem", color: "text.secondary" }}
										>
											{`Currently in ${current}`}
										</Typography>
									</Box>
								</Stack>
							);
						})}
						{visibleActions.length === 0 && (
							<Typography
								sx={{ fontSize: "0.8125rem", color: "text.secondary", p: 2 }}
							>
								{`No action matches “${filter}”.`}
							</Typography>
						)}
					</Box>
				</Box>
			</Box>
		</ConfigEditorShell>
	);
};

export default GroupEditor;
